import { ref, computed, onMounted } from "vue";
import type { Ref } from "vue";
import type { KanaStepA } from "../types";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import { getWordList } from "@/common/graphql/expression";
import { getCityByPrefGroupedByStepA } from "@/common/graphql/cityByPref";
import { getChainInfo } from "@/common/graphql/chain";
import { MEMBER_LIST_FILE_NAME } from "../constants";

interface UseMemberInitParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  wordList: Ref<Record<string, string>>;
  prefSelectValue: Ref<string>;
  originalAddress: Ref<string>;
  cityBlockList: Ref<KanaStepA | null | undefined>;
  initTableHeaders: (wordList: Record<string, string>, house_card_display: number) => void;
}

/**
 * 画面表示時に必要な初期化処理を提供する composable。
 *
 * @returns 初期化処理を実行する関数群と初期表示に必要な状態
 */
export const useMemberInit = (params: UseMemberInitParams) => {
  const {
    errdialog,
    errMessage,
    wordList,
    prefSelectValue,
    originalAddress,
    cityBlockList,
    initTableHeaders,
  } = params;

  // Fieldのname属性で使用される必要なラベルキー（OperatorRegister.vueと同様のパターン）
  const requiredFieldLabelKeys = [
    'lbl_member_list',
    'lbl_search',
    'lbl_name_kanji_kana',
    'lbl_tel',
    'lbl_no_data_text',
    'lbl_zipcode',
    'lbl_house_card',
    'lbl_member_id',
    'lbl_delivery_destination_address_detail',
    'lbl_room_num',
    'lbl_family_name_kanji',
    'lbl_first_name_kanji',
    'lbl_family_name_kana',
    'lbl_first_name_kana',
    'lbl_memo_member',
    'lbl_other_block_disp_name',
    'lbl_other_block_select',
    'lbl_area_not_select',
  ] as const;

  const isWordListReady = computed(() => {
    if (Object.keys(wordList.value).length === 0) return false;
    return requiredFieldLabelKeys.every((key) => key in wordList.value);
  });

  const house_card_display = ref(0);
  const house_card_validation = ref("");
  const house_card_required = ref(0);

  //本日日付
  const now = ref(new Date());
  const nowDate = ref(
    now.value.getFullYear() +
      "-" +
      ("0" + String(Number(now.value.getMonth()) + 1)).slice(-2) +
      "-" +
      ("0" + now.value.getDate()).slice(-2)
  );

  const isDisabled = ref(false);
  const statusSelectedItem = ref(["5", "10"]);

  /**
   * 初期処理
   */
  const initialize = async (): Promise<void> => {
  };

  onMounted(async () => {
    try {
      //初期化処理
      await initialize();

      //文言データを取得する
      wordList.value = await getWordList(
        localStorageGetChainCode(),
        MEMBER_LIST_FILE_NAME,
        localStorageGetCustomerCode()
      );

      /**
       * 都道府県から住所を取得
       */
      originalAddress.value = prefSelectValue.value;

      if (prefSelectValue.value !== "") {
        cityBlockList.value = await getCityByPrefGroupedByStepA(
          localStorageGetCustomerCode(),
          localStorageGetChainCode(),
          localStorageGetShopCode(),
          prefSelectValue.value
        );
      }

      //チェーン情報を取得する
      const result = await getChainInfo(
        localStorageGetCustomerCode(),
        localStorageGetChainCode()
      );
      if (result !== undefined && result !== null) {
        house_card_display.value = result.house_card_display
          ? result.house_card_display
          : 0;
        house_card_validation.value = result.house_card_validation
          ? result.house_card_validation
          : "";
        if (
          house_card_validation.value !== "" &&
          house_card_validation.value.indexOf("required") !== -1
        ) {
          house_card_required.value = 1;
        }
      }

      // 会員一覧 ヘッダー を初期化
      initTableHeaders(wordList.value, house_card_display.value);
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value =
        err instanceof Error ? err.message : "予期せぬエラーが発生しました。";
      return;
    }
  });

  return {
    isWordListReady,
    house_card_display,
    house_card_validation,
    house_card_required,
    nowDate,
    isDisabled,
    statusSelectedItem,
  };
};
